"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { getCurrentUser } from "@/lib/auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createClubRsvp, fetchClubRsvpCounts, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { ClubRsvpStatus } from "@/types/club";

export function EventRsvpForm({ eventId }: { eventId: string }) {
  const client = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<ClubRsvpStatus>("going");
  const [message, setMessage] = useState<string | null>(null);
  const user = useQuery({ queryKey: ["club-user"], queryFn: getCurrentUser });
  useEffect(() => {
    if (user.data) setEmail(user.data.email);
  }, [user.data]);
  const counts = useQuery({
    queryKey: ["club-rsvp-counts", eventId],
    queryFn: () => fetchClubRsvpCounts(eventId),
  });

  const save = useMutation({
    mutationFn: () =>
      createClubRsvp({
        eventId,
        guestName: name.trim(),
        guestEmail: email.trim(),
        status,
      }),
    onSuccess: async () => {
      setMessage("RSVP saved.");
      await client.invalidateQueries({ queryKey: ["club-rsvp-counts", eventId] });
    },
    onError: (cause) => setMessage(formatApiError(cause, "RSVP could not be saved.")),
  });

  if (user.isLoading) return <p role="status">Loading account...</p>;
  if (!user.data) return (
    <Link href="/auth/magic-link" className="mt-6 inline-flex min-h-[44px] items-center underline">
      Sign in by email to RSVP
    </Link>
  );

  return (
    <section className="mt-10 border-t border-[var(--color-border)] pt-8">
      <h2 className="text-xl font-semibold">RSVP</h2>
      {counts.data && (
        <p className="mt-2 text-sm text-slate-600">
          {counts.data.going} going · {counts.data.maybe} maybe · {counts.data.declined} declined
        </p>
      )}
      <form
        className="mt-5 grid gap-4 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <div>
          <Label htmlFor="rsvpName">Name *</Label>
          <Input id="rsvpName" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="rsvpEmail">Email *</Label>
          <Input
            id="rsvpEmail"
            type="email"
            required
            readOnly
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="rsvpStatus">Response</Label>
          <select
            id="rsvpStatus"
            value={status}
            onChange={(e) => setStatus(e.target.value as ClubRsvpStatus)}
            className="mt-1 min-h-[44px] w-full rounded-[var(--border-radius)] border border-slate-300 bg-white px-3 text-sm"
          >
            <option value="going">Going</option>
            <option value="maybe">Maybe</option>
            <option value="declined">Declined</option>
          </select>
        </div>
        <div className="flex items-end">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Send RSVP"}
          </Button>
        </div>
      </form>
      {message && (
        <p role="status" className="mt-3 text-sm text-slate-700">
          {message}
          {save.isError && <Link href="/auth/magic-link" className="block min-h-[44px] py-3 underline">Verify your email</Link>}
        </p>
      )}
    </section>
  );
}
